import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Filter, PlayCircle } from 'lucide-react';
import { ProspectListCard } from '@/components/ProspectListCard';
import { ProspectListCreator } from '@/components/ProspectListCreator';
import { ProspectListView } from '@/components/ProspectListView';
import { useOptimizedCampaignData } from '@/contexts/OptimizedCampaignDataContext';
import { useToast } from '@/hooks/use-toast';
import { TrainingVideoModal } from '@/components/TrainingVideoModal';
import { useLocationTrainingVideo } from '@/hooks/useLocationTrainingVideo';


export default function ProspectManagement() {
  const { prospectLists, deleteProspectList, addProspectList } = useOptimizedCampaignData();
  const { toast } = useToast();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [complianceFilter, setComplianceFilter] = useState<'all' | 'compliant' | 'pending'>('all');
  const [showCreator, setShowCreator] = useState(false);
  const [editingList, setEditingList] = useState<any>(null);
  const [viewingList, setViewingList] = useState<any>(null);

  const filteredLists = prospectLists.filter(list => {
    const matchesSearch = list.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (list.description?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCompliance = complianceFilter === 'all' || 
                            (complianceFilter === 'compliant' && list.tcpa_compliance) ||
                            (complianceFilter === 'pending' && !list.tcpa_compliance);
    
    return matchesSearch && matchesCompliance;
  });

  /*
  * Update the current viewingList whenever a prospect is added,edited or removed
  */
  useEffect(() => {
    if (viewingList){
      for (const list of prospectLists){
        if (list.id === viewingList.id){
          setViewingList(list);
          break;
        }
      }
    }
  }, [prospectLists]);

  const handleEdit = (list: any) => {
    setEditingList(list);
    setShowCreator(true);
  };

  const handleDuplicate = (list: any) => {
    const duplicatedList = {
      name: `${list.name} (Copy)`,
      description: list.description,
      prospects: [...list.prospects],
      tcpa_compliance: false, // Require new TCPA acknowledgment
      tcpa_acknowledged_at: undefined,
      tcpa_acknowledged_by: undefined
    };
    
    addProspectList(duplicatedList as any);
    toast({
      title: "List Duplicated",
      description: "A copy of the list has been created. Compliance acknowledgement must be re-acknowledged.",
    });
  };

  const handleDelete = (id: string) => {
    const list = prospectLists.find(l => l.id === id);
    if (list && confirm(`Are you sure you want to delete "${list.name}"? This action cannot be undone.`)) {
      deleteProspectList(id);
      toast({
        title: "List Deleted",
        description: "Prospect list has been deleted successfully.",
      });
    }
  };

  const handleView = (list: any) => {
    setViewingList(list);
  };

  const handleCloseCreator = () => {
    setShowCreator(false);
    setEditingList(null);
  };

  const handleListCreated = (newList: any) => {
    console.log('DEBUG: handleListCreated called with:', newList);
    // Display the newly created list directly
    setViewingList(newList);
    console.log('DEBUG: setViewingList called with:', newList);
  };

  const handleCloseView = () => {
    setViewingList(null);
  };

  const { hasVideo, displayTitle } = useLocationTrainingVideo("prospects");

  if (viewingList) {
    return (
      <ProspectListView 
        prospectList={viewingList} 
        onBack={handleCloseView}
        readOnly={false}
        complianceLabelMode="friendly"
      />
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Prospect Management</h1>
          <p className="text-muted-foreground">Create, manage, and organize your prospect lists</p>
          
          {hasVideo && (
            <div className="mt-4">
              <TrainingVideoModal
                trigger={
                  <Button variant="outline" size="sm">
                    <PlayCircle className="h-4 w-4 mr-2" />
                    {displayTitle || "View Training"}
                  </Button>
                }
                locationKey="prospects"
              />
            </div>
          )}
        </div>
        
        <Button 
          onClick={() => setShowCreator(true)} 
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Create New List
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Search & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search lists by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select 
              value={complianceFilter} 
              onValueChange={(value: 'all' | 'compliant' | 'pending') => setComplianceFilter(value)}
            >
              <SelectTrigger className="w-[200px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Lists</SelectItem>
                <SelectItem value="compliant">Compliance Acknowledged</SelectItem>
                <SelectItem value="pending">Compliance Needed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Lists Grid */}
      {filteredLists.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredLists.map((list) => (
            <ProspectListCard
              key={list.id}
              prospectList={list as any}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              onView={handleView}
              readOnly={false}
              complianceLabelMode="friendly"
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
                <Plus className="h-8 w-8 text-muted-foreground" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  {prospectLists.length === 0 ? 'No prospect lists yet' : 'No lists match your search'}
                </h3>
                <p className="text-muted-foreground">
                  {prospectLists.length === 0 
                    ? 'Create your first prospect list to get started' 
                    : 'Try adjusting your search or filters'
                  }
                </p>
              </div>
              {prospectLists.length === 0 && (
                <Button onClick={() => setShowCreator(true)} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Create Your First List
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* List Creator Dialog */}
      <ProspectListCreator
        open={showCreator}
        onOpenChange={handleCloseCreator}
        editingList={editingList}
        onListCreated={handleListCreated}
      />
    </div>
  );
}